import { Component } from '@theme/component';

/**
 * Custom cookie consent banner — a branded shell over Shopify's Customer
 * Privacy API. Renders a floating card in the bottom-left corner on first
 * visit and lets the shopper accept or reject all tracking categories.
 *
 * The element starts with the native `hidden` attribute. After Shopify's
 * consent-tracking-api loads, we check `shouldShowBanner()` and reveal the
 * card if the visitor hasn't decided yet.
 *
 * Public API:
 *   document.querySelector('aromely-cookie-banner').show()
 *     Force the banner to reappear — wire this up from a "Manage cookies"
 *     link in the footer. Safe to call at any time, even after the visitor
 *     previously dismissed.
 *
 * @extends {Component<{}>}
 */
class AromelyCookieBanner extends Component {
  /** @type {number|undefined} */
  #dismissTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#initConsentApi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#dismissTimeout) clearTimeout(this.#dismissTimeout);
  }

  /**
   * Wait for Shopify's consent-tracking-api to be available (it may already
   * be loaded via content_for_header, or we have to load it ourselves) then
   * decide whether to show the banner.
   */
  #initConsentApi() {
    const onReady = () => {
      const cp = window.Shopify?.customerPrivacy;
      if (!cp) return;

      // Check currentVisitorConsent() — if any category is already 'yes'
      // or 'no', the visitor has already responded via the Shopify cookie.
      try {
        const consent = cp.currentVisitorConsent?.();
        if (consent) {
          const decided = Object.values(consent).some(v => v === 'yes' || v === 'no');
          if (decided) return;
        }
      } catch {}

      // Region-aware gate — shouldShowBanner() returns false when consent
      // tracking isn't required for the visitor's region.
      try {
        if (cp.shouldShowBanner?.()) {
          this.show();
        }
      } catch (err) {
        console.warn('Cookie banner: shouldShowBanner() failed', err);
      }
    };

    if (window.Shopify?.customerPrivacy) {
      onReady();
      return;
    }

    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (err) => {
          if (err) {
            console.warn('Cookie banner: failed to load consent API', err);
            return;
          }
          onReady();
        }
      );
    }
  }

  /**
   * Reveal the banner and trigger the slide-in animation. Safe to call
   * repeatedly — bumping between `hidden`/`data-open` restarts the animation
   * so a "Manage cookies" link can re-invoke show() after a prior dismissal.
   */
  show() {
    if (this.#dismissTimeout) {
      clearTimeout(this.#dismissTimeout);
      this.#dismissTimeout = undefined;
    }
    this.removeAttribute('hidden');
    this.removeAttribute('data-closing');
    // Force a reflow between removing `hidden` and setting `data-open` so
    // the browser doesn't coalesce the state change and skip the transition.
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight;
    this.setAttribute('data-open', '');
  }

  /**
   * Accept all consent categories, then dismiss.
   */
  handleAccept() {
    this.#setConsent({
      analytics: true,
      marketing: true,
      preferences: true,
      sale_of_data: true,
    });
  }

  /**
   * Reject all consent categories, then dismiss.
   */
  handleReject() {
    this.#setConsent({
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false,
    });
  }

  /**
   * @param {object} consent
   */
  #setConsent(consent) {
    const cp = window.Shopify?.customerPrivacy;
    if (!cp?.setTrackingConsent) {
      this.#dismiss();
      return;
    }
    try {
      cp.setTrackingConsent(consent, (err) => {
        if (err) console.warn('Cookie banner: setTrackingConsent failed', err);
        this.#dismiss();
      });
    } catch (err) {
      console.warn('Cookie banner: setTrackingConsent threw', err);
      this.#dismiss();
    }
  }

  /**
   * Run the fade-out animation, then re-apply the `hidden` attribute so the
   * element is fully removed from the accessibility tree.
   */
  #dismiss() {
    this.removeAttribute('data-open');
    this.setAttribute('data-closing', '');
    if (this.#dismissTimeout) clearTimeout(this.#dismissTimeout);
    this.#dismissTimeout = setTimeout(() => {
      this.setAttribute('hidden', '');
      this.removeAttribute('data-closing');
      this.#dismissTimeout = undefined;
    }, 250);
  }
}

if (!customElements.get('aromely-cookie-banner')) {
  customElements.define('aromely-cookie-banner', AromelyCookieBanner);
}
